import { CheckCircle } from '@c/icons/CheckCircle';
import type { BreadcrumbItem } from '@ui/Breadcrumb';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import Select from '@ui/Select';
import { useWarehouseVariations } from '@util/hooks/useWarehouseVariations';
import { distance, isMobile } from '@util/index';
import { slugify } from '@util/urlHelpers';
import DesktopSearchByVehicle from 'app/product/[slug]/components/SearchByVehicle/DesktopSearchByVehicle';
import MobileSearchByVehicle from 'app/product/[slug]/components/SearchByVehicle/MobileSearchByVehicle';
import { useWarehouse } from 'app/product/[slug]/components/WarehouseProvider';
import { getBikeFitment } from 'app/product/utils/getBikeFitment';
import { filterFittingItems } from 'app/product/utils/vehicleSpecificDirect';
import {
  getInStockValues,
  shouldHideSoldOutValues,
} from 'app/product/utils/warehouseVariationVisibility';
import { useAuth } from 'context/AuthContext';
import { useBikeFilter, type TemporaryBike } from 'context/BikeFilterContext';
import {
  AlertCircleIcon,
  ArrowRightIcon,
  WrenchIcon,
  XCircleIcon,
} from 'lucide-react';
import { ProductDocument, VariationKey } from 'models/product';
import { UserBike } from 'models/shared';
import { useCallback, useState, type ReactNode } from 'react';
import { BADGE_CONFIG } from './InformationBadge';

const sizeSort = [
  'XXXS',
  'XXS',
  'XS',
  'S',
  'SM',
  'M',
  'MD',
  'L',
  'LG',
  'XL',
  'XXL',
  '2XL',
  '3XL',
  '4XL',
  '5XL',
  '6XL',
  '7XL',
  '8XL',
  '9XL',
  '10XL',
];

const fullNameToSizeKey: Record<string, string> = {
  'XXX-SMALL': 'XXXS',
  'XX-SMALL': 'XXS',
  'X-SMALL': 'XS',
  'EXTRA SMALL': 'XS',
  SMALL: 'S',
  MEDIUM: 'M',
  LARGE: 'L',
  'X-LARGE': 'XL',
  'EXTRA LARGE': 'XL',
  'XX-LARGE': 'XXL',
  'XXX-LARGE': '3XL',
};

const getFirstSizeKey = (size: string) => {
  const trimmed = size.trim();
  const mapped = fullNameToSizeKey[trimmed.toUpperCase()];
  if (mapped) return mapped;
  return trimmed.includes('-')
    ? trimmed.split('-')[0].trim()
    : trimmed.includes('/')
    ? trimmed.split('/')[0].trim()
    : trimmed;
};

function FitmentMessage({
  selectedBike,
  fitmentStatus,
  onClear,
}: {
  selectedBike: TemporaryBike;
  fitmentStatus: 'fits' | 'may-fit' | 'does-not-fit' | undefined;
  onClear: () => void;
}) {
  const vehicleLabel = [
    selectedBike.year,
    selectedBike.make,
    selectedBike.model,
  ]
    .filter(Boolean)
    .join(' ');

  const renderMessage = (bg: string, icon: ReactNode, title: string) => (
    <div
      className={`flex min-h-[6rem] w-full items-center justify-between gap-x-4 rounded-brand px-6 py-3 ${bg}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-x-4">
        <div className="shrink-0">{icon}</div>
        <div className="flex min-w-0 flex-col">
          <span className="text-[1.6rem] font-semibold leading-tight text-black">
            {title}
          </span>
          <span className="break-words text-[1.4rem] leading-snug text-zinc-600">
            {vehicleLabel}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="shrink-0 text-[1.6rem] font-medium text-black underline"
      >
        Clear
      </button>
    </div>
  );

  if (fitmentStatus === 'fits') {
    return renderMessage(
      'bg-green-500/10',
      <CheckCircle
        className="h-[2.5rem] w-[2.5rem]"
        fill={BADGE_CONFIG.success.foreground}
      />,
      'Fits your vehicle'
    );
  }

  if (fitmentStatus === 'may-fit') {
    return renderMessage(
      'bg-yellow-500/10',
      <AlertCircleIcon
        className="h-[2.5rem] w-[2.5rem]"
        style={{ color: BADGE_CONFIG.warning.foreground }}
      />,
      'May fit your vehicle'
    );
  }

  if (fitmentStatus === 'does-not-fit') {
    return renderMessage(
      'bg-red-500/10',
      <XCircleIcon
        className="h-[2.5rem] w-[2.5rem]"
        style={{ color: BADGE_CONFIG.error.foreground }}
      />,
      'Does not fit your vehicle'
    );
  }

  return null;
}

export default function ProductWarehouseSelector({
  product,
  breadcrumbs,
  onSelection,
}: {
  product: ProductDocument;
  breadcrumbs?: BreadcrumbItem[];
  onSelection: (item: ProductDocument) => void;
}) {
  const { userDoc } = useAuth();
  const { selectedBike, setSelectedBike, clearSelectedBike } = useBikeFilter();
  const { setUseGroupTitle } = useWarehouse();

  // Selecting a color/size listing exits group-title mode so the specific
  // product title (and MPN) stick — same outcome as a bike fitment match.
  const selectListing = useCallback(
    (item: ProductDocument) => {
      setUseGroupTitle(false);
      onSelection(item);
    },
    [onSelection, setUseGroupTitle]
  );

  // Fetch variations and relatedItems from backend
  const { variations, relatedItems } = useWarehouseVariations(product);

  const fittingItems = filterFittingItems(relatedItems, selectedBike);
  const options = variations.options.filter((option) => {
    return fittingItems.some((item) => item.id === option.id);
  });
  const keys = new Set(
    options.flatMap((option) => option.values.map((value) => value.key))
  );
  const values = new Map<string, Set<string | number>>();
  options.forEach((option) => {
    option.values.forEach((value) => {
      if (!values.has(value.key)) {
        values.set(value.key, new Set<string | number>());
      }
      values.get(value.key)?.add(value.value);
    });
  });
  const variationKeys = Array.from(keys)
    .map((key) => {
      const v = Array.from(values.get(key) ?? [])
        .filter((val) => val !== '' && val !== null && val !== undefined)
        .sort((a, b) => {
          if (typeof a === 'string' && typeof b === 'string') {
            return a.localeCompare(b);
          } else if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
          } else {
            return a.toString().localeCompare(b.toString());
          }
        });

      // Don't show if there are no values or only one value
      if (v.length === 0 || v.length === 1) return null;

      return {
        key,
        label: key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        values: v,
      };
    })
    .filter((key): key is VariationKey => key !== null);

  const hideSoldOut = shouldHideSoldOutValues(variationKeys, options.length);
  const inStockValues = getInStockValues(options, fittingItems);

  // Initialize selections from product defaults
  // Spec keys will be populated as user makes selections, and Select components have fallbacks
  const [selections, setSelections] = useState<{
    [key: string]: string | number;
  }>(() => {
    const initialSelections: { [key: string]: string | number } = {};

    // Add color if present
    if (product.color) {
      initialSelections.color = product.color;
    }

    // Add sizes if present
    if (product.size?.letter) {
      initialSelections.letter_size = product.size.letter;
    }
    if (product.size?.number) {
      initialSelections.number_size = product.size.number;
    }

    // add specs, case
    keys.forEach((key) => {
      // skip color
      if (key === 'color' || key === 'letter_size' || key === 'number_size')
        return;
      const spec = Object.keys(product.specs ?? {}).find(
        (k) =>
          k.toLowerCase().replace(/ /g, '_') ===
          key.toLowerCase().replace(/ /g, '_')
      );
      if (spec && product.specs) {
        initialSelections[key] = product.specs[spec];
      }
      // if the key doesn't exist in the product.specs, add it with the value 'N/A'
      if (!spec && product.specs) {
        initialSelections[key] = 'empty';
      }
    });
    return initialSelections;
  });
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);
  const [showDesktopSearchByVehicle, setShowDesktopSearchByVehicle] =
    useState(false);

  const handleBikeComplete = (bike: UserBike) => {
    if (
      bike.type &&
      bike.make &&
      bike.model &&
      bike.year &&
      relatedItems.length > 0
    ) {
      setSelectedBike(bike);
      const item = relatedItems.find((item) =>
        item.fitting_bikes?.bikes?.some((b) => {
          return (
            b.make === bike.make &&
            b.model === bike.model &&
            b.year === bike.year
          );
        })
      );
      if (item) {
        selectListing(item);
      } else {
        // find one without the year
        const item = relatedItems.find((item) =>
          item.fitting_bikes?.bikes?.some((b) => {
            return b.make === bike.make && b.model === bike.model;
          })
        );
        if (item) {
          selectListing(item);
        } else {
          // find the closest match by model
          const scores = relatedItems.map((item) => {
            let heighestScore = 0;
            item.fitting_bikes?.bikes?.forEach((b) => {
              const score = distance(b.model, bike.model, {
                caseSensitive: false,
              });
              if (score > heighestScore) {
                heighestScore = score;
              }
            });
            return [item.id, heighestScore] as const;
          });
          const closestMatch = scores.sort((a, b) => b[1] - a[1])[0];
          if (closestMatch) {
            const item = relatedItems.find(
              (item) => item.id === closestMatch[0]
            );
            selectListing(item!);
          } else {
            alert('No item found for the selected bike');
          }
        }
      }
    }
  };

  // A universal part carries no fitting bikes, which would otherwise resolve to
  // a "does not fit" message for every vehicle.
  const fitment =
    selectedBike.type && !product.fitting_bikes?.universal
      ? getBikeFitment(product.fitting_bikes?.bikes ?? [], [
          selectedBike as UserBike,
        ])
      : null;

  const isBikeComplete = Boolean(
    selectedBike.type &&
      selectedBike.make &&
      selectedBike.year &&
      selectedBike.model
  );

  if (
    relatedItems.some((item) => item.fitting_bikes?.bikes?.length) &&
    !isBikeComplete &&
    !relatedItems.some((item) =>
      ['Tires', 'Grips'].includes(item.category2 ?? '')
    )
  ) {
    return (
      <div className="flex flex-col gap-y-6">
        <div className="flex w-full flex-col gap-y-8">
          {/* Vehicle Selection Banner */}
          <div className="relative flex min-h-[8rem] w-full flex-col items-start gap-4 rounded-b-[1rem] border-b-2 border-l-2 border-r-2 border-t-2 border-b-gray-300 border-l-gray-300 border-r-gray-300 border-t-brand-secondary px-4 pb-8 pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-x-4">
            <div className="flex items-center gap-x-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yellow-400">
                <WrenchIcon className="h-6 w-6 text-white" />
              </div>
              <div className="flex flex-col gap-y-1">
                <span className="text-[1.6rem] font-bold text-black">
                  This product is vehicle specific.
                </span>
                <span className="text-[1.6rem] text-black">
                  Select a vehicle to ensure fitment.
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                if (isMobile()) {
                  setShowMobileSearchByVehicle(true);
                } else {
                  setShowDesktopSearchByVehicle(true);
                }
              }}
              className="w-full whitespace-nowrap rounded-full bg-black px-8 py-5 text-[1.4rem] font-semibold uppercase tracking-wide text-white transition-colors hover:bg-gray-800 sm:w-auto sm:py-3"
            >
              SELECT YOUR VEHICLE
            </button>
          </div>

          <MobileSearchByVehicle
            show={showMobileSearchByVehicle}
            dismiss={() => setShowMobileSearchByVehicle(false)}
            onBikeComplete={handleBikeComplete}
            userDoc={userDoc}
          />

          <DesktopSearchByVehicle
            isOpen={showDesktopSearchByVehicle}
            dismiss={() => setShowDesktopSearchByVehicle(false)}
            onBikeComplete={handleBikeComplete}
          />
        </div>
      </div>
    );
  }

  const handleSelectionChange = (key: string, value: string | number) => {
    // reset on mounting position change
    const newSelections =
      key === 'mounting_position'
        ? {
            [key]: value,
          }
        : {
            ...selections,
            [key]: value,
          };
    for (const [key, value] of Object.entries(newSelections)) {
      if (value === '') {
        delete newSelections[key as keyof typeof newSelections];
      }
    }
    // Try to find a matching variation with current selections
    const matchingVariations = options.filter((option) => {
      // find the variation option that matches all current selections
      if (option.values.length < Object.keys(newSelections).length)
        return false;
      return option.values.every((val) => {
        return (
          newSelections[val.key as keyof typeof newSelections]?.toString() ===
          val.value.toString()
        );
      });
    });

    if (matchingVariations.length > 0) {
      const matchingItems = fittingItems
        .filter((item) =>
          matchingVariations.some((variation) => variation.id === item.id)
        )
        .sort((a, b) => b.stock - a.stock);
      selectListing(matchingItems[0]);
      setSelections(newSelections);
    } else {
      // unset all other selections

      if (key === 'color') {
        const foundItem = fittingItems.find(
          (item) => item.color === value && !item.out_of_stock
        );
        const fallbackItem = fittingItems[0];
        selectListing(foundItem ?? fallbackItem);
        setSelections({
          [key]: value,
        });
      } else if (key === 'letter_size' || key === 'number_size') {
        selectListing(
          fittingItems.find(
            (item) =>
              (item.size?.letter === value ||
                item.size?.number === Number(value)) &&
              !item.out_of_stock
          ) ?? fittingItems[0]
        );
        setSelections({
          [key]: value,
        });
      } else {
        const item = fittingItems.find(
          (item) =>
            Object.entries(item.specs ?? {}).some(
              ([k, v]) =>
                key.toLowerCase().replace(/ /g, '_') ===
                  k.toLowerCase().replace(/ /g, '_') &&
                v.toString().toLowerCase() === value.toString().toLowerCase()
            ) && !item.out_of_stock
        );
        if (item) {
          selectListing(item);
          setSelections({
            [key]: value,
          });
        }
      }
    }
  };

  // A sold-out value is dropped from its axis only on a group crowded enough to
  // bury what is still buyable, and never when it is the current pick.
  const isValueVisible = (
    key: string,
    value: string | number,
    selectedValue?: string
  ) => {
    if (!hideSoldOut) return true;
    const valueStr = value.toString();
    if (selectedValue === valueStr) return true;
    return inStockValues.get(key)?.has(valueStr) ?? false;
  };

  // Build color to image map for color selector
  const getColorSelections = () => {
    const colorToImageMap = new Map<string, string>();
    fittingItems.forEach((item) => {
      if (item.color && !colorToImageMap.has(item.color)) {
        colorToImageMap.set(
          item.color,
          item.thumbnail ?? `no-image-${slugify(item.color)}`
        );
      }
    });
    return Array.from(colorToImageMap.entries()).map(([color, image]) => ({
      color,
      image,
    }));
  };

  // Get sorted sizes for size selector
  const getSortedSizes = (sizeValues: (string | number)[]) => {
    return [...sizeValues].sort((a, b) => {
      if (typeof a === 'string' && typeof b === 'string') {
        const aKey = getFirstSizeKey(a);
        const bKey = getFirstSizeKey(b);
        const aIdx = sizeSort.indexOf(aKey);
        const bIdx = sizeSort.indexOf(bKey);

        if (aIdx !== -1 && bIdx !== -1) {
          return aIdx - bIdx;
        }
        return a.localeCompare(b);
      } else if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
      } else {
        return a.toString().localeCompare(b.toString());
      }
    });
  };

  // Check if product is a tire
  const isTire = () => product.category2 === 'Tires';

  // Get filtered tire size values based on selected mounting position
  const getFilteredTireSizeValues = (
    tireSizeKey: string,
    allValues: (string | number)[]
  ) => {
    // Find the mounting position key
    const mountingPositionKey = variationKeys.find(
      (k) =>
        k.key.toLowerCase().replace(/ /g, '_') === 'mounting_position' ||
        k.key === 'Mounting Position'
    );

    if (!mountingPositionKey) return allValues;

    const selectedMountingPosition =
      selections[mountingPositionKey.key] ||
      product.specs?.[mountingPositionKey.label];
    if (!selectedMountingPosition) return allValues;

    // Filter options to only include those with the selected mounting position
    const filteredOptions = options.filter((option) => {
      const hasMountingPosition = option.values.some(
        (val) =>
          (val.key.toLowerCase().replace(/ /g, '_') === 'mounting_position' ||
            val.key === 'Mounting Position') &&
          val.value.toString() === selectedMountingPosition.toString()
      );
      return hasMountingPosition;
    });

    // Get tire size values from filtered options
    // Find the tire size key in variationKeys to get the exact key format
    const tireSizeVariationKey = variationKeys.find(
      (k) =>
        (k.key.toLowerCase().replace(/ /g, '_').includes('tire') &&
          k.key.toLowerCase().replace(/ /g, '_').includes('size')) ||
        (k.label.toLowerCase().includes('tire') &&
          k.label.toLowerCase().includes('size'))
    );

    if (!tireSizeVariationKey) return allValues;

    const filteredTireSizeValues = new Set<string | number>();
    filteredOptions.forEach((option) => {
      option.values.forEach((val) => {
        // Match tire size key (case-insensitive, handle spaces/underscores)
        const valKeyNormalized = val.key.toLowerCase().replace(/ /g, '_');
        const tireSizeKeyNormalized = tireSizeVariationKey.key
          .toLowerCase()
          .replace(/ /g, '_');
        if (
          valKeyNormalized === tireSizeKeyNormalized ||
          val.key === tireSizeVariationKey.key
        ) {
          filteredTireSizeValues.add(val.value);
        }
      });
    });

    const filtered = Array.from(filteredTireSizeValues).filter(
      (val) => val !== '' && val !== null && val !== undefined
    );

    return filtered.sort();
  };

  return (
    <div className="flex flex-col gap-y-6">
      {variationKeys.map((key) => {
        // Check if this is a tire size key for tires (need to check early)
        const keyNormalized = key.key.toLowerCase().replace(/ /g, '_');
        const labelNormalized = key.label.toLowerCase();
        const isTireSizeKey =
          isTire() &&
          ((keyNormalized.includes('tire') && keyNormalized.includes('size')) ||
            (labelNormalized.includes('tire') &&
              labelNormalized.includes('size')));

        // Don't render if there are no values or only one value (except for tire sizes)
        if (!key.values || key.values.length === 0) return null;
        if (!isTireSizeKey && key.values.length === 1) return null;

        // Filter out empty values for additional safety
        const validValues = key.values.filter(
          (val) => val !== '' && val !== null && val !== undefined
        );
        if (validValues.length === 0) return null;
        if (!isTireSizeKey && validValues.length === 1) return null;

        // Render color selector for color key
        if (key.key === 'color') {
          const colorSelections = getColorSelections();
          const selectedColor =
            selections[key.key]?.toString() || product.color || '';
          const visibleColors = colorSelections.filter((selection) =>
            isValueVisible(key.key, selection.color, selectedColor)
          );
          if (visibleColors.length === 0) return null;

          return (
            <div key={key.key} className="flex flex-col gap-y-6">
              <span className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
                {key.label} {selectedColor ? `: ${selectedColor}` : ''}
              </span>
              <div className="flex flex-wrap gap-2">
                {visibleColors.map((selection) => {
                  const { color, image } = selection;
                  const isSelected = selectedColor === color;

                  // Check if this color is available (has at least one item in stock)
                  const selectedSize =
                    selections.letter_size || selections.number_size;
                  const isColorUnavailable = relatedItems
                    .filter(
                      (item) =>
                        item.color === color &&
                        (item.size?.letter === selectedSize ||
                          item.size?.number === Number(selectedSize))
                    )
                    .every((item) => item.out_of_stock);

                  let optimizedImage = image;
                  if (image.startsWith('https://cdn.wpsstatic.com/images/')) {
                    optimizedImage = image.replace(
                      /\/(500|1000)_max\//g,
                      '/200_max/'
                    );
                  }

                  return (
                    <button
                      type="button"
                      key={`color-${color}`}
                      className={`relative flex h-[6rem] w-fit min-w-[10rem] items-center justify-center rounded-[1rem] border px-2 md:h-[5rem] md:min-w-[8rem] ${
                        isSelected
                          ? 'border-brand-secondary'
                          : 'border-zinc-200'
                      } ${
                        isColorUnavailable
                          ? 'bg-gray-200 opacity-40 before:absolute before:left-1/2 before:top-1/2 before:z-10 before:h-[2px] before:w-[90%] before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:bg-red-500/70 before:content-[""]'
                          : 'cursor-pointer hover:border-brand-secondary'
                      } overflow-hidden font-medium text-zinc-800 transition-colors duration-150`}
                      onClick={() => {
                        handleSelectionChange(key.key, color);
                      }}
                      title={color}
                    >
                      {image.includes('no-image-') ? (
                        <span className="flex h-full w-full items-center justify-center text-center">
                          {color}
                        </span>
                      ) : (
                        <SafeImage
                          src={optimizedImage}
                          key={optimizedImage}
                          alt={`${color}`}
                          className="mx-auto h-full w-[7rem] rounded-[1rem] object-contain"
                          height={100}
                          width={100}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }

        // Render size selector for letter_size or number_size keys
        if (key.key === 'letter_size' || key.key === 'number_size') {
          const sortedSizes = getSortedSizes(validValues);
          const selectedSize =
            selections[key.key]?.toString() ||
            (key.key === 'letter_size'
              ? product.size?.letter?.toString()
              : product.size?.number?.toString()) ||
            '';
          const visibleSizes = sortedSizes.filter((size) =>
            isValueVisible(key.key, size, selectedSize)
          );
          if (visibleSizes.length === 0) return null;

          return (
            <div key={key.key} className="flex flex-col gap-y-6">
              <span className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
                Size
              </span>
              <div className="flex flex-wrap gap-4">
                {visibleSizes.map((size) => {
                  const sizeStr = size.toString();
                  const isSelected = selectedSize === sizeStr;

                  const selectedColor = selections.color;
                  const isSizeUnavailable = fittingItems
                    .filter(
                      (item) =>
                        (item.size?.letter === sizeStr ||
                          item.size?.number === Number(sizeStr)) &&
                        item.color === selectedColor
                    )
                    .every((item) => item.out_of_stock);
                  return (
                    <button
                      type="button"
                      key={`size-${sizeStr}`}
                      onClick={() => {
                        handleSelectionChange(key.key, size);
                      }}
                      className={`relative flex h-[6rem] w-fit min-w-[10rem] items-center justify-center rounded-[1rem] border px-2 md:h-[5rem] md:min-w-[8rem] ${
                        isSelected
                          ? 'border-brand-secondary'
                          : 'border-zinc-200'
                      } ${
                        isSizeUnavailable
                          ? 'bg-gray-200 opacity-40 before:absolute before:left-1/2 before:top-1/2 before:z-10 before:h-[2px] before:w-[90%] before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:bg-red-500/70 before:content-[""]'
                          : 'cursor-pointer hover:border-brand-secondary'
                      } overflow-hidden font-medium text-zinc-800 transition-colors duration-150`}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }

        // Render box selector for Mounting Position key
        if (key.key === 'mounting_position') {
          const selectedValue =
            selections[key.key]?.toString() ||
            product.specs?.[key.label]?.toString() ||
            '';
          const visibleValues = validValues.filter((value) =>
            isValueVisible(key.key, value, selectedValue)
          );
          if (visibleValues.length === 0) return null;

          return (
            <div key={key.key} className="flex flex-col gap-y-6 ">
              <span className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
                {key.label}
              </span>
              <div className="flex  gap-4">
                {visibleValues.map((value) => {
                  const valueStr = value.toString();
                  const isSelected = selectedValue === valueStr;

                  return (
                    <button
                      type="button"
                      key={`mounting-${valueStr}`}
                      onClick={() => {
                        handleSelectionChange(key.key, value);
                      }}
                      className={`flex h-[6rem] w-full min-w-[10rem] items-center justify-center rounded-[1rem] border px-2 md:h-[5rem] md:min-w-[8rem] ${
                        isSelected
                          ? 'border-brand-secondary'
                          : 'border-zinc-200'
                      } cursor-pointer overflow-hidden font-medium text-zinc-800 transition-colors duration-150 hover:border-brand-secondary`}
                    >
                      {valueStr}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }

        // Render regular Select for other keys
        const selectedValue =
          selections[key.key]?.toString() ||
          product[key.key as keyof ProductDocument]?.toString() ||
          product.specs?.[key.label]?.toString() ||
          '';

        // Get filtered values if this is a tire size key
        const axisValues = isTireSizeKey
          ? getFilteredTireSizeValues(key.key, validValues)
          : validValues;
        const valuesToUse = axisValues.filter((value) =>
          isValueVisible(key.key, value, selectedValue)
        );

        // Don't render if filtered values are empty
        if (valuesToUse.length === 0) {
          // If we filtered out all values, still show the dropdown but with a message
          if (isTireSizeKey) {
            return (
              <div key={key.key}>
                <span className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
                  {key.label}
                </span>
                <div className="text-[1.4rem] text-zinc-600">
                  Please select a mounting position first
                </div>
              </div>
            );
          }
          return null;
        }

        // Don't render if only one value (except for tire sizes)
        if (!isTireSizeKey && valuesToUse.length === 1) {
          return null;
        }

        const optionsToUse = valuesToUse
          .map((value) => {
            const newSelections = {
              ...selections,
              [key.key]: value,
            };
            const isOutOfStock =
              options.filter((option) => {
                if (option.values.length < Object.keys(newSelections).length)
                  return false;
                return option.values.every((val) => {
                  return (
                    newSelections[val.key]?.toString() === val.value.toString()
                  );
                });
              }).length === 0;
            if (isOutOfStock) return null;
            return {
              id: value.toString(),
              label: value.toString(),
              value: value.toString(),
            };
          })
          .filter(Boolean) as {
          id: string;
          label: string;
          value: string;
        }[];
        const hasAnyOptions = optionsToUse.length > 0;
        if (!hasAnyOptions) return null;
        return (
          <div key={key.key}>
            <span className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
              {key.label}
            </span>
            <Select
              emptyLabel="N/A"
              options={optionsToUse}
              value={
                // A filtered tire size only keeps the current value when it
                // survived the mounting-position filter
                isTireSizeKey &&
                selectedValue &&
                !valuesToUse.some((v) => v.toString() === selectedValue)
                  ? ''
                  : selectedValue
              }
              onChange={(option) => {
                handleSelectionChange(key.key, option.value);
              }}
            />
          </div>
        );
      })}

      {selectedBike && product.category === 'Bike Parts' && (
        <>
          <div className="flex w-full sm:justify-end">
            <FitmentMessage
              selectedBike={selectedBike}
              fitmentStatus={fitment?.status}
              onClear={clearSelectedBike}
            />
          </div>
          {fitment?.status === 'does-not-fit' && !!breadcrumbs?.length && (
            <Button
              type="outline"
              href={`${
                breadcrumbs[1] ? breadcrumbs[1].href : '/shop/all'
              }?bikes=${selectedBike.year}:${selectedBike.make}:${
                selectedBike.model
              }`}
            >
              <span className="sm:hidden">Find parts</span>
              <span className="hidden sm:inline">Find parts that will fit</span>
              <ArrowRightIcon className="h-5 w-5" />
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// export default function ProductWarehouseSelector({
//   product,
//   onSelection,
// }: {
//   product: ProductDocument;
//   onSelection: (item: ProductDocument) => void;
// }) {
//   const { data: relatedItems } = useQuery<ProductDocument[] | null>({
//     queryKey: ['warehouse_items', product.integration_info?.remote_id],
//     queryFn: async () => {
//       const warehouse_id = product.integration_info?.remote_id;
//       if (!warehouse_id) return null;
//       const snap = await getDocs(
//         query(
//           productsRef,
//           where('integration_info.remote_id', '==', warehouse_id)
//         )
//       );
//       return snap.docs.map((doc) => doc.data()) as ProductDocument[];
//     },
//   });
//   if (!relatedItems) return null;
//   const colors = new Set(
//     relatedItems.map((item) => item.color).filter(Boolean)
//   );
//   const sizes = new Set(
//     relatedItems
//       .map((item) => item.size?.letter || item.size?.number)
//       .filter(Boolean)
//   );

//   const variations = relatedItems.map((item) => getLabelFromItems(item));
//   const uniqueVariations = [...new Set(variations)].sort();
//     relatedItems.filter((item) => item.specs?.['Tooth Count'] === '46 Tooth')
//   );
//   const isOutOfStock = (item?: ProductDocument) => {
//     return !item || item.out_of_stock ? ' (Out of Stock)' : '';
//   };
//   return (
//     <div className="flex max-w-[40rem] flex-col gap-y-6">
//       {colors.size > 1 && (
//         <ColorSelector
//           onSelection={(color) => {
//             const item = relatedItems.find(
//               (item) =>
//                 item.color === color &&
//                 getLabelFromItems(item) === getLabelFromItems(product)
//             );
//             if (item) {
//               onSelection(item);
//             } else {
//               const item = relatedItems.find(
//                 (item) =>
//                   item.color === color &&
//                   !item.thumbnail.includes('coming-soon')
//               );
//               if (item) {
//                 onSelection(item);
//               }
//             }
//           }}
//           selections={
//             Array.from(colors)
//               .map((color) => {
//                 const variation = relatedItems.find(
//                   (item) =>
//                     item.color === color &&
//                     !item.thumbnail.includes('coming-soon')
//                 );
//                 if (!variation) return null;
//                 return { color, image: variation.thumbnail ?? '' };
//               })
//               .filter(Boolean) as { color: string; image: string }[]
//           }
//           selectedColor={product.color ?? ''}
//           isUnavailable={false}
//         />
//       )}
//       {uniqueVariations.length > 1 && (
//         <>
//           <h3 className="text-[1.8rem] font-medium text-black sm:text-[2.2rem] md:text-[1.8rem]">
//             Please select a variation
//           </h3>
//           <Select
//             allowEmpty={false}
//             options={uniqueVariations.map((variation) => ({
//               id: variation,
//               label:
//                 variation +
//                 isOutOfStock(
//                   relatedItems.find(
//                     (item) =>
//                       getLabelFromItems(item) === variation &&
//                       item.color === product.color
//                   )
//                 ),
//               value: variation,
//             }))}
//             onChange={(option) => {
//               const id = option.id as string;
//               const item = relatedItems.find((item) => {
//                 const label = getLabelFromItems(item);
//                 if (label === id) {
//                 } else {
//                 }
//                 return label === id && item.color === product.color;
//               });
//               if (item) {
//                 onSelection(item);
//               }
//             }}
//             value={getLabelFromItems(product)}
//             placeholder="Select a variation"
//             className="w-full"
//             rounded
//             grow
//           />
//         </>
//       )}
//     </div>
//   );
// }

// const uniqueMpns = [
//   ...new Set(relatedItems.map((item) => item.mpn).flat()),
// ].filter(Boolean) as string[];

// const mpnToVariation = new Map<
//   string,
//   {
//     color: string | null;
//     size: string | number | null;
//     image: string | null;
//   }
// >();
// for (const mpn of uniqueMpns) {
//   if (mpnToVariation.has(mpn)) continue;
//   const firstItem = relatedItems.find((item) => item.mpn?.includes(mpn));
//   if (firstItem) {
//     mpnToVariation.set(mpn, {
//       color: firstItem.color ?? null,
//       size: firstItem.size?.letter ?? firstItem.size?.number ?? null,
//       image: firstItem.thumbnail ?? null,
//     });
//   }
// }
// const colors = Array.from(mpnToVariation.values()).map((variation) => ({
//   color: variation.color,
//   image: variation.image ?? 'no-image-' + slugify(variation.color ?? ''),
// }));
// const uniqueColors = [...new Set(colors.map((color) => color.color))];

// const getFirstSizeKey = (size: string) => size.split('-')[0].trim();
// const sizeSort = [
//   'XXXS',
//   'XXS',
//   'XS',
//   'S',
//   'SM',
//   'M',
//   'MD',
//   'L',
//   'LG',
//   'XL',
//   'XXL',
//   '2XL',
//   '3XL',
//   '4XL',
//   '5XL',
//   '6XL',
//   '7XL',
//   '8XL',
//   '9XL',
//   '10XL',
// ];
// const sizes = Array.from(mpnToVariation.values())
//   .map((variation) => variation.size)
//   .filter(Boolean) as (string | number)[];
// const uniqueSizes = [...new Set(sizes)];
// return (
//   <>
//     <ColorSelector
//       onSelection={(color) => {
//       }}
//       selections={
//         uniqueColors
//           .map((color) => {
//             const variation = colors.find((c) => c.color === color);
//             if (!variation) return null;
//             return variation;
//           })
//           .filter(Boolean) as { color: string; image: string }[]
//       }
//       isSelected={false}
//       isUnavailable={false}
//     />
//     <SizeSelector
//       onSelection={(size) => {
//       }}
//       selections={
//         uniqueSizes
//           .map((size) => {
//             const variation = sizes.find((s) => s === size);
//             if (!variation) return null;
//             return size;
//           })
//           .filter(Boolean) as (string | number)[]
//       }
//       isGearCombo={false}
//     />
//   </>
// );
// }
